import { cva } from 'class-variance-authority';
import { Flex, Icon } from '@nula/core-ui';
import type { SurfaceEntry } from '@/lib/sync/document-surfaces';
import styles from './surface-row-controls.module.css';

interface SurfaceRowControlsProps {
  entry: SurfaceEntry;
  isFirst: boolean;
  isLast: boolean;
  onMove: (direction: 'up' | 'down') => void;
  /** Opens this surface's detail face (rename, delete, and whatever follows). */
  onOpenDetail: () => void;
}

const btn = cva(styles.btn);

/**
 * The trailing controls on a surface row: move up, move down, then a `⋯` that
 * opens the surface's detail face.
 *
 * Reordering is the one spatial action — it needs the list visible and the
 * neighbour adjacent — so it stays here rather than behind the `⋯`, where
 * every nudge would cost an open and a close. Everything else is a discrete
 * choice and lives on the face, which keeps the row from growing as actions
 * are added.
 *
 * The arrows disable at the ends rather than unmounting: a row that changes
 * width as it reaches the top or bottom of the list shifts the controls under
 * the pointer mid-reorder.
 */
export function SurfaceRowControls({
  entry, isFirst, isLast, onMove, onOpenDetail,
}: SurfaceRowControlsProps) {
  return (
    <Flex align="center" className={styles.group}>
      <button
        type="button"
        aria-label={`Move ${entry.title} up`}
        disabled={isFirst}
        onClick={(e) => { e.stopPropagation(); onMove('up'); }}
        className={btn()}
      >
        <Icon name="arrow_upward" size={16} />
      </button>
      <button
        type="button"
        aria-label={`Move ${entry.title} down`}
        disabled={isLast}
        onClick={(e) => { e.stopPropagation(); onMove('down'); }}
        className={btn()}
      >
        <Icon name="arrow_downward" size={16} />
      </button>

      {/* Groups the two spatial actions apart from the menu — without it three
          identical squares read as one undifferentiated cluster. */}
      <Flex component="span" className={styles.separator} aria-hidden="true" />

      <button
        type="button"
        aria-label={`${entry.title} options`}
        onClick={(e) => { e.stopPropagation(); onOpenDetail(); }}
        className={btn()}
      >
        <Icon name="more_horiz" size={16} />
      </button>
    </Flex>
  );
}
